import React, { useEffect, useMemo, useState } from 'react';
import { BILL_CATEGORIES, categoryOf, Expense } from '../data/expenses';

/** Format a number as HKD (e.g. HK$1,234.50) using the device locale. */
export function formatHKD(amount: number): string {
  return new Intl.NumberFormat('en-HK', {
    style: 'currency',
    currency: 'HKD',
  }).format(amount);
}

/**
 * Distinct slice colors, assigned to bills in sorted list order. The same
 * color is used for the bill's row in the breakdown below, so each slice can
 * be matched to its bill at a glance.
 */
const CHART_COLORS = [
  '#f59e0b', // orange
  '#8b5cf6', // violet
  '#10b981', // emerald
  '#3b82f6', // blue
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // deep orange
  '#6366f1', // indigo
  '#84cc16', // lime
  '#06b6d4', // cyan
  '#a855f7', // purple
  '#ef4444', // red
];

function chartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

/** Row color for a bill without an amount — grey, not in the pie. */
const NO_AMOUNT_COLOR = '#c9c5d6';

/**
 * Does this device report a real hover pointer? Touch iPhones report
 * `(hover: none)`, so we must NOT pre-select on mouseenter there — otherwise
 * the tap's `click` event would immediately toggle the selection back off.
 * (jsdom has no matchMedia; default to desktop-like behaviour so hover tests
 * exercise the mouse path.)
 */
function supportsHover(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: hover)').matches
    : true;
}

/**
 * Height of the sticky app header, so the sticky total bar (v0.13) pins itself
 * right below it instead of sliding underneath. Measured at mount (the header
 * is already rendered when the totals view mounts) and re-measured on resize.
 */
function useHeaderOffset(): number {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('.app-header');
      setOffset(header ? header.getBoundingClientRect().height : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return offset;
}

/**
 * Sort for the breakdown (v0.12.2): bills WITH an amount first, bills without
 * an amount last; within each group, earlier due dates on top (empty dates
 * sink to the bottom of their group).
 */
function sortBreakdown(items: Expense[]): Expense[] {
  return [...items].sort((a, b) => {
    const aHas = typeof a.amount === 'number';
    const bHas = typeof b.amount === 'number';
    if (aHas !== bHas) return aHas ? -1 : 1;
    const da = a.date || '9999-12-31';
    const db = b.date || '9999-12-31';
    return da.localeCompare(db);
  });
}

interface ExpenseSummaryProps {
  /** Bills of the currently viewed month. */
  items: Expense[];
}

interface PieChartProps {
  items: Expense[];
  total: number;
  activeIndex: number | null;
  canHover: boolean;
  onActiveChange: (index: number | null) => void;
}

/**
 * Donut chart of the month's bill amounts: one slice per bill that has an
 * amount, each in its own colour, with the month total in the centre (modern
 * two-line label + gradient amount). Hovering a slice (mouse) or tapping it
 * (touch) shows the bill's name in the caption below and highlights the
 * matching row further down. Drawn with plain SVG (stroke-dasharray on
 * circles — no chart dependency).
 */
const PieChart: React.FC<PieChartProps> = ({ items, total, activeIndex, canHover, onActiveChange }) => {
  // r ≈ 15.915 gives a circumference of ~100, so dash lengths are percentages.
  const R = 15.915;
  let acc = 0; // cumulative % already drawn, in path order (clockwise from 12 o'clock)

  return (
    <svg
      viewBox="0 0 42 42"
      className="summary-chart"
      role="img"
      aria-label="Bill amounts breakdown"
      data-testid="summary-chart"
    >
      <defs>
        <linearGradient id="chart-total-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8b7cf0" />
          <stop offset="1" stopColor="#6c5ce7" />
        </linearGradient>
      </defs>
      <g transform="rotate(-90 21 21)">
        <circle cx="21" cy="21" r={R} fill="none" className="summary-chart-track" strokeWidth="6" />
        {total > 0 &&
          items.map((e, i) => {
            if (typeof e.amount !== 'number') return null;
            const pct = (e.amount / total) * 100;
            const isActive = activeIndex === i;
            const el = (
              <circle
                key={e.id}
                cx="21"
                cy="21"
                r={R}
                fill="none"
                className="summary-chart-slice"
                stroke={chartColor(i)}
                strokeWidth={isActive ? 7.5 : 6}
                strokeDasharray={`${Math.max(pct, 0)} ${Math.max(100 - pct, 0)}`}
                strokeDashoffset={-acc}
                opacity={activeIndex === null || isActive ? 1 : 0.3}
                onMouseEnter={canHover ? () => onActiveChange(i) : undefined}
                onMouseLeave={canHover ? () => onActiveChange(null) : undefined}
                onClick={() => onActiveChange(canHover ? i : isActive ? null : i)}
              />
            );
            acc += pct;
            return el;
          })}
      </g>
      <text x="21" y="17.5" textAnchor="middle" className="summary-chart-total-label">
        TOTAL
      </text>
      <text
        x="21"
        y="24"
        textAnchor="middle"
        dominantBaseline="central"
        className="summary-chart-total"
        fill="url(#chart-total-grad)"
      >
        {formatHKD(total)}
      </text>
    </svg>
  );
};

/**
 * Total-expense view: a colour-coded donut chart of the month's bill amounts.
 * Bills with an amount sort first (earliest due date on top), bills without
 * an amount sink to the bottom. Bills without an amount get no slice and a
 * neutral grey dot.
 */
const ExpenseSummary: React.FC<ExpenseSummaryProps> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const canHover = useMemo(supportsHover, []);
  const sorted = useMemo(() => sortBreakdown(items), [items]);
  const headerOffset = useHeaderOffset();

  const total = sorted.reduce(
    (sum, e) => sum + (typeof e.amount === 'number' ? e.amount : 0),
    0
  );
  const hasAnyAmount = sorted.some((e) => typeof e.amount === 'number');

  const activeItem = activeIndex != null ? sorted[activeIndex] : null;
  const activePct =
    activeItem && typeof activeItem.amount === 'number' && total > 0
      ? Math.round((activeItem.amount / total) * 1000) / 10
      : null;

  const dateLabel = (date: string): string => {
    if (!date) return 'no date';
    const d = new Date(`${date}T00:00:00`);
    if (isNaN(d.getTime())) return date;
    return d.toLocaleDateString('en-HK', { day: 'numeric', month: 'short' });
  };

  // Category groups (v0.14): only when at least one bill has a category. Each
  // group keeps the bills in flat `sorted` order, and each row keeps the colour
  // of its flat index so slice ↔ row colour matching stays intact.
  const groups = useMemo(() => {
    if (!items.some((e) => e.category)) return null;
    return BILL_CATEGORIES.map((cat) => ({
      category: cat,
      items: sorted.filter((e) => categoryOf(e) === cat),
    })).filter((g) => g.items.length > 0);
  }, [items, sorted]);

  const renderRow = (e: Expense, flatIndex: number) => {
    const color = typeof e.amount === 'number' ? chartColor(flatIndex) : NO_AMOUNT_COLOR;
    const isActive = activeIndex === flatIndex;
    return (
      <li
        key={e.id}
        className={`summary-row ${isActive ? 'summary-row--active' : ''}`}
        style={{
          background: `${color}${isActive ? '26' : '14'}` /* tint of the slice colour */,
          boxShadow: isActive ? `0 0 0 2px ${color}` : undefined,
        }}
        onMouseEnter={canHover ? () => setActiveIndex(flatIndex) : undefined}
        onMouseLeave={canHover ? () => setActiveIndex(null) : undefined}
        onClick={() => setActiveIndex(canHover ? flatIndex : isActive ? null : flatIndex)}
      >
        <span className="summary-row-name">
          <span className="summary-row-title-line">
            <span
              className="summary-row-dot"
              style={{ background: color }}
              aria-hidden="true"
            />
            <span className="summary-row-title">{e.title || 'Untitled bill'}</span>
          </span>
          <span className="summary-row-date">{dateLabel(e.date)}</span>
        </span>
        <span className="summary-row-amount">
          {typeof e.amount === 'number' ? formatHKD(e.amount) : '—'}
        </span>
      </li>
    );
  };

  return (
    <div className="summary">
      {items.length === 0 ? (
        <p className="empty-state">No bills this month.</p>
      ) : !hasAnyAmount ? (
        <p className="empty-state">
          No amounts yet — tap a bill, fill in its amount (HK$), and it will show up here.
        </p>
      ) : (
        <>
          <PieChart
            items={sorted}
            total={total}
            activeIndex={activeIndex}
            canHover={canHover}
            onActiveChange={setActiveIndex}
          />
          <p className="summary-chart-tip" aria-live="polite">
            {activeItem
              ? `${activeItem.title || 'Untitled bill'} — ${
                  typeof activeItem.amount === 'number'
                    ? `${formatHKD(activeItem.amount)} (${activePct}% of total)`
                    : 'no amount'
                }`
              : 'Hover or tap a slice to see which bill it is'}
          </p>
          <div
            className="summary-total-bar"
            style={{ top: headerOffset }}
            role="status"
            data-testid="summary-total-bar"
          >
            <span className="summary-total-bar-label">Total this month</span>
            <span className="summary-total-bar-amount">{formatHKD(total)}</span>
          </div>
          {groups ? (
            <ul className="summary-list">
              {groups.map((g) => (
                <li key={g.category} className="summary-category">
                  <div className="summary-category-head">
                    <span className="summary-category-name">{g.category}</span>
                    <span className="summary-category-total">
                      {formatHKD(
                        g.items.reduce(
                          (s, e) => s + (typeof e.amount === 'number' ? e.amount : 0),
                          0
                        )
                      )}
                    </span>
                  </div>
                  <ul className="summary-category-list">
                    {g.items.map((e) => renderRow(e, sorted.indexOf(e)))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="summary-list">
              {sorted.map((e, i) => renderRow(e, i))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default ExpenseSummary;
